import type { ChatDeliveryReceipt } from "./types";

const FINGERPRINT = /^[a-f0-9]{64}$/;
const UUID = /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i;

export function isChatDeliveryReceipt(value: unknown): value is ChatDeliveryReceipt {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.turn_id === "string" &&
    UUID.test(item.turn_id) &&
    [item.request_fingerprint, item.answer_fingerprint, item.signature].every(
      (part) => typeof part === "string" && FINGERPRINT.test(part)
    ) &&
    Number.isSafeInteger(item.attempt_count) &&
    Number(item.attempt_count) > 0 &&
    Number.isSafeInteger(item.answer_length) &&
    Number(item.answer_length) > 0 &&
    Number(item.answer_length) <= 200_000
  );
}

export function withDeliveryAcknowledgement(body: unknown): unknown {
  return body && typeof body === "object" && !Array.isArray(body)
    ? { ...body, delivery_ack_version: 1 }
    : body;
}

/** Confirm only a fully received answer. Never retry generation on ack failure. */
export async function acknowledgeChatDelivery(
  chatUrl: string,
  answer: string,
  delivery: unknown,
  headers?: HeadersInit,
  signal?: AbortSignal
): Promise<void> {
  // Python len and Array.from both count Unicode code points, including emoji.
  // The capability arrives after the answer on the ordered response stream;
  // length validation also refuses dropped, duplicated or partial text frames.
  if (
    signal?.aborted ||
    !isChatDeliveryReceipt(delivery) ||
    Array.from(answer).length !== delivery.answer_length
  )
    return;
  const endpoint = `${chatUrl.split("?")[0].replace(/\/$/, "")}/delivery`;
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Content-Type", "application/json");
  requestHeaders.set("Accept", "application/json");
  const body = JSON.stringify(delivery);
  for (let attempt = 0; attempt < 2; attempt++) {
    if (signal?.aborted) return;
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const timer = setTimeout(abort, 5_000);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: requestHeaders,
        body,
        signal: controller.signal,
      });
      // Duplicates are settled idempotently by the ledger; expired/stale
      // receipts and other reached 4xx failures must never be retried away.
      if (response.ok || response.status < 500) return;
    } catch {
      // Only this same receipt retries. A lost ack ultimately expires/refunds;
      // it must not turn a received answer into another paid generation.
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    }
  }
}
